/* eslint-disable no-unused-vars */
/* eslint-disable default-case */
import React, { useState } from 'react';
import {
  Container, Box, Typography, Button, Grid, Paper,
  TextField, Stepper, Step, StepLabel, CircularProgress,
  alpha, useTheme, Checkbox, FormControlLabel
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BarChartIcon from '@mui/icons-material/BarChart';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TimelineIcon from '@mui/icons-material/Timeline';
import { motion, AnimatePresence } from 'framer-motion';
import useIsMobile from '../hooks/useIsMobile';

function CreateAccount() {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    company_name: '',
    phone_number: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA'
  });

  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const features = [
    {
      icon: <DirectionsCarIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Inventory Management',
      description: 'Track every vehicle from acquisition to sale with detailed history and documentation.',
    },
    {
      icon: <BarChartIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Real-time Analytics',
      description: 'Make data-driven decisions with comprehensive reporting and insights.',
    },
    {
      icon: <ReceiptIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Expense Tracking',
      description: 'Monitor all costs and expenses with our advanced OCR technology.',
    },
    {
      icon: <TimelineIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Performance Metrics',
      description: 'Track profitability and performance across your entire inventory.',
    },
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePasswordChange = async (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'confirmPassword' && formData.password !== value) {
      setPasswordError('Passwords do not match');
    } else if (name === 'password' && formData.confirmPassword && formData.confirmPassword !== value) {
      setPasswordError('Passwords do not match');
    } else {
      setPasswordError('');
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    if (formData.password !== formData.confirmPassword) {
      setMessage('Passwords do not match');
      setPasswordError('Passwords do not match');
      return;
    }
    setPasswordError('');

    setIsLoading(true);
    setMessage('');
    console.log('Attempting registration...'); // Log start

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      console.log('Registration response status:', response.status); // Log status

      // Check if the request was successful (status code 2xx)
      if (response.ok) {
        try {
          const data = await response.json();
          console.log('Registration success data:', data); // Log success data
          if (data.payment_link && typeof data.payment_link === 'string' && data.payment_link.startsWith('http')) {
            console.log('Payment link found, redirecting:', data.payment_link);
            // Redirect immediately to the Stripe Checkout page
            window.location.href = data.payment_link;
            // Set message just before redirecting (might not be visible for long)
            setMessage('Redirecting to payment...');
            // Stop further execution in this function after redirect starts
            return;
          } else {
            // Handle case where response is OK but link is missing or invalid
            console.error('Payment link missing or invalid in successful response:', data);
            setMessage('Registration successful, but payment link is missing. Please contact support.');
          }
        } catch (jsonError) {
           // Handle cases where the successful response body isn't valid JSON
           console.error('Error parsing JSON from successful response:', jsonError);
           setMessage('Received an invalid response from server. Please contact support.');
        }
      } else {
        // Handle error responses (status code 4xx or 5xx)
        let errorMsg = `Registration failed with status: ${response.status}`;
        try {
          // Try to get a more specific error message from the response body
          const errorData = await response.json();
          console.log('Registration error data:', errorData); // Log error data
          errorMsg = errorData.error || errorData.message || errorMsg;
        } catch (jsonError) {
          // If the error response body isn't JSON, stick with the status code message
          console.error('Could not parse JSON from error response:', jsonError);
        }
        console.error('Registration request failed:', errorMsg);
        setMessage(errorMsg);
      }
    } catch (networkError) {
      // Handle network errors (e.g., server down, CORS issues, DNS problems)
      console.error('Registration fetch/network error:', networkError);
      setMessage('Network error during registration. Please check connection and try again.');
    } finally {
      // Ensure loading indicator is turned off regardless of outcome
      setIsLoading(false);
    }
  };

  const steps = ['Plan Overview', 'Account Details', 'Company Information'];

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'transparent', borderRadius: 2 }}>
              <Typography
                variant="h4"
                gutterBottom
                align="center"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
                  color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.grey[800],
                  mb: 1,
                }}
              >
                Premium Dealership Management Suite
              </Typography>

              <Typography
                variant="h5"
                align="center"
                sx={{
                  color: theme.palette.success.dark,
                  fontWeight: 600,
                  mb: 2,
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                }}
              >
                $99/month
              </Typography>

              <Typography
                variant="body1"
                align="center"
                color="text.secondary"
                sx={{ mb: 5, maxWidth: '600px', mx: 'auto' }}
              >
                Everything you need to manage your dealership efficiently, bundled into one powerful platform.
              </Typography>

              <Grid container spacing={isMobile ? 2 : 3}>
                {features.map((feature, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 3,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        bgcolor: alpha(theme.palette.primary.light, 0.05),
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                        borderRadius: 2,
                        transition: 'box-shadow 0.3s ease-in-out',
                        '&:hover': {
                           boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                           borderColor: alpha(theme.palette.primary.main, 0.4),
                        }
                      }}
                    >
                      <Box sx={{
                        mb: 2,
                        p: 1.5,
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: 'inline-flex'
                       }}>
                        {React.cloneElement(feature.icon, { sx: { fontSize: 32, color: theme.palette.primary.main } })}
                      </Box>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </motion.div>
        );
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Account Details
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handlePasswordChange}
                  required
                  error={!!passwordError}
                  helperText={passwordError}
                  disabled={isLoading}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  error={!!passwordError}
                  helperText={passwordError}
                  disabled={isLoading}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
            </Grid>
          </motion.div>
        );
      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Company Information
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  name="street_address"
                  value={formData.street_address}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
            </Grid>
          </motion.div>
        );
      default:
        return null;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      style={{
        display: 'flex',
        minHeight: 'calc(100vh - 64px)',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.palette.mode === 'dark'
          ? `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`
          : `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.1)} 0%, ${alpha(theme.palette.secondary.light, 0.1)} 100%)`,
        padding: '16px',
      }}
    >
      <Container maxWidth="md" sx={{
        py: { xs: 3, sm: 4 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
      }}>
        <Paper
          elevation={4}
          sx={{
            p: { xs: 2, sm: 4, md: 5 },
            borderRadius: 3,
            background: theme.palette.mode === 'dark' ? theme.palette.background.paper : 'white',
            color: theme.palette.mode === 'dark' ? 'white' : 'inherit',
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            width: '100%',
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <Stepper
            activeStep={activeStep}
            alternativeLabel={!isMobile}
            orientation={isMobile ? "vertical" : "horizontal"}
            sx={{
              mb: { xs: 3, sm: 5 },
              '& .MuiStepLabel-label': {
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                fontWeight: 500,
              },
              '& .MuiStepIcon-root': {
                color: theme.palette.grey[400],
                '&.Mui-active': {
                  color: theme.palette.primary.main,
                  transform: 'scale(1.1)',
                  transition: 'transform 0.2s ease-in-out',
                },
                '&.Mui-completed': {
                  color: theme.palette.success.main,
                },
              },
              '& .MuiStepConnector-line': {
                 borderColor: theme.palette.grey[300],
              },
              '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
                 borderColor: theme.palette.primary.main,
              },
              '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
                 borderColor: theme.palette.success.main,
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <AnimatePresence mode="wait">
            {renderStepContent(activeStep)}
          </AnimatePresence>

          {message && (
            <Typography
              color={message.toLowerCase().includes('failed') || message.toLowerCase().includes('error') || message.toLowerCase().includes('missing') ? 'error' : 'info'}
              align="center"
              sx={{ mt: 3, minHeight: '1.5em', fontWeight: 500 }}
            >
              {message}
            </Typography>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 5 }}>
            <Button
              disabled={activeStep === 0 || isLoading}
              onClick={handleBack}
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              sx={{ borderRadius: 2 }}
            >
              Back
            </Button>
            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  size={isMobile ? "small" : "medium"}
                  sx={{ borderRadius: 2, px: 3 }}
                >
                  {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Create Account & Pay'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  size={isMobile ? "small" : "medium"}
                  sx={{ borderRadius: 2, px: 3 }}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>
    </motion.div>
  );
}

export default CreateAccount;